import { useContext, useEffect, useState } from 'react';
import gtag from '../analytics/gtag';
import { getProduct } from '../api/product';
import type { MiniCartProduct } from '../cart/CartContext';
import { CartContext, MiniCartContext } from '../cart/CartContext';
import { giftCardBalanceCheckUrl } from '../config/SiteConfig';
import type { ErrorBoxType } from '../generic/ErrorBox';
import ErrorBox from '../generic/ErrorBox';
import Page from '../generic/Page';
import TabButton, { TabGroup } from '../generic/TabButton';
import { WrapTrackingContext } from '../generic/TrackingContext';
import InputGroup from '../generic/forms/InputGroup';
import type { Product } from '../model/types';
import { BuyButton } from '../product/AddToCartAction';
import { productImageToSrcSet } from '../product/ProductImage';
import QuantityPicker from '../product/QuantityPicker';
import eventTargetValue from '../utils/eventTargetValue';
import preventDefault from '../utils/preventDefault';
import useBooleanState from '../utils/useBooleanState';
import * as style from './GiftCardPage.module.css';

export interface Props {
    productId: 'egavekort' | 'gavekort';
    egavekort?: Product;
    gavekort?: Product;
}

export default function GiftcardPage({ productId, egavekort: initialEgavekort, gavekort: initialGavekort }: Props) {
    const [error, setError] = useState<ErrorBoxType | null>(null);
    const [egavekort, setEgavekort] = useState(initialEgavekort);
    const [gavekort, setGavekort] = useState(initialGavekort);
    const [selectedProduct, setSelectedProduct] = useState(productId);

    const product = selectedProduct === 'egavekort' ? egavekort : gavekort;

    const miniCart = useContext(MiniCartContext);
    const inCart = miniCart?.getProduct(selectedProduct);

    useEffect(() => {
        if (product) {
            if (product.url !== location.pathname) {
                history.replaceState(null, '', product.url);
            }

            gtag('event', 'view_item', {
                items: [{ item_id: product.code }],
            });
        } else {
            switch (selectedProduct) {
                case 'egavekort':
                    getProduct('egavekort').then(setEgavekort).catch(setError);
                    break;
                case 'gavekort':
                    getProduct('gavekort').then(setGavekort).catch(setError);
                    break;
            }
        }
    }, [selectedProduct, product]);

    const imageUrl = product?.images?.find((i) => i.format === 'product')?.url;
    return (
        <Page title="Gavekort" error={error}>
            <TabGroup aria-label="Velg digitalt gavekort eller fysisk gavekort">
                <WrapTrackingContext set={{ tab_name: 'gavekort' }}>
                    <TabButton selected={selectedProduct === 'egavekort'} onSelect={() => setSelectedProduct('egavekort')} large>
                        Digitalt gavekort
                    </TabButton>
                    <TabButton selected={selectedProduct === 'gavekort'} onSelect={() => setSelectedProduct('gavekort')} large>
                        Fysisk gavekort
                    </TabButton>
                </WrapTrackingContext>
            </TabGroup>
            <div className={style.giftcard}>
                <img
                    className={style.giftcardImage}
                    src={imageUrl}
                    alt={`Bilde av produktet ${product?.name}`}
                    srcSet={productImageToSrcSet(imageUrl, [150, 300, 600, 900, 1200, 1500, 1800])}
                    sizes="(max-width: 768px) 240px, 320px"
                />

                {selectedProduct === 'gavekort' ? (
                    <p className={style.conditions}>Gavekortet blir sendt fra oss i løpet av 1-2 virkedager.</p>
                ) : (
                    <p className={style.conditions}>Gavekortet blir sendt til din e-postadresse og er gyldig i tre år.</p>
                )}
                {inCart ? <GiftCardInCart {...inCart} maxQuantity={product?.maxQuantity} /> : <BuyGiftCard productCode={selectedProduct} />}
            </div>
            <p className={style.conditions}>
                <a href={giftCardBalanceCheckUrl()}>Sjekk saldo på gavekort</a>
                {' | '}
                <a href="/kundeservice/gaver-gavekort">Informasjon om gavekort</a>
            </p>
        </Page>
    );
}

interface GiftCardInCartProps extends MiniCartProduct {
    maxQuantity?: number;
}

function GiftCardInCart({ price: { readableValue, formattedValue, value }, code, entryNumber, quantity, maxQuantity }: GiftCardInCartProps) {
    return (
        <div className={style.giftcardForm}>
            <div className={style.price} aria-label={readableValue}>
                {formattedValue}
            </div>
            <WrapTrackingContext set={{ value, currency: 'NOK', item_id: code }}>
                <QuantityPicker entryNumber={entryNumber} quantity={quantity} maxQuantity={maxQuantity} />
            </WrapTrackingContext>
        </div>
    );
}

function BuyGiftCard({ productCode }: { productCode: string }) {
    const [error, setError] = useState<ErrorBoxType | null>(null);
    const [amountStr, setAmountStr] = useState('');
    const [isLoading, setIsLoading] = useBooleanState(false);

    const cart = useContext(CartContext);

    const addToCart = async () => {
        setError(null);

        const amount = parseInt(amountStr);

        if (isNaN(amount)) {
            setError('Angi et gyldig beløp mellom 100 kr og 10 000 kr');
            return;
        }

        if (amount < 100) {
            setError('Beløp må være større enn eller lik 100 kr');
            return;
        }

        if (amount > 10000) {
            setError('Beløp må være mindre enn eller lik 10 000 kr');
            return;
        }

        setIsLoading.toTrue();

        try {
            await cart.addToCart(productCode, 1, amount);

            gtag('event', 'add_to_cart', {
                value: amount,
                currency: 'NOK',
                items: [{ quantity: 1, item_id: productCode }],
            });
        } catch (error: any) {
            setError(error);
        } finally {
            setIsLoading.toFalse();
        }
    };

    return (
        <form onSubmit={preventDefault(addToCart)} className={style.giftcardForm}>
            <div className={style.btnGroup}>
                {['200', '300', '400', '500', '800', '1000'].map((amount) => (
                    <button type="button" key={amount} className={style.btnGroupBtn} onClick={() => setAmountStr(amount)}>
                        {amount} kr
                    </button>
                ))}
            </div>
            <InputGroup
                label="Beløp (maks 10 000)"
                size={7}
                type="number"
                name="price"
                value={amountStr}
                min={100}
                max={10_000}
                onChange={eventTargetValue(setAmountStr)}
            />
            <BuyButton isLoading={isLoading} />
            <ErrorBox errors={error} />
        </form>
    );
}
